// src/services/api.js - Centralized API service with ultra-secure encryption only
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Security levels
export const SECURITY_LEVELS = {
  ULTRA_SECURE: 'ultra_secure'
};

class APIService {
  constructor() {
    // Always use ultra-secure encryption for logged-in users
    console.log('🔐 API Service initialized with ultra-secure encryption only');
    
    // Setup axios interceptor for 401 errors
    this.setupAxiosInterceptors();
  }

  setupAxiosInterceptors() {
    // Add a response interceptor to handle 401 errors globally
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.warn('🕒 JWT Token expired - redirecting to login');
          
          // Store current path to redirect back after login
          const currentPath = window.location.pathname;
          if (currentPath !== '/login' && currentPath !== '/register') {
            localStorage.setItem('redirectAfterLogin', currentPath);
          }
          
          // Clear expired token and redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('userInfo');
          
          // Show user-friendly message
          alert('Din session har gått ut. Du kommer att omdirigeras till inloggningssidan.');
          
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  getHeaders(token) {
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // Send chat message with ultra-secure encryption only
  async sendChatMessage(message, token, preloadedContext = null, lifeThemeContext = null, symptomContext = null, newConversation = false, feelingScore = null) {
    // Always use ultra-secure chat for logged-in users
    const endpoint = '/api/ultra-secure-chat/ultra-secure-chat';

    console.log(`🔐 Sending message via ultra-secure encryption to ${endpoint}`);
    if (preloadedContext) {
      console.log(`🚀 OPTIMIZATION: Including ${preloadedContext.length} pre-loaded messages in request`);
    }
    if (lifeThemeContext) {
      console.log(`🎯 LIFE THEME: Including theme context for "${lifeThemeContext.themeName}"`);
    }
    if (symptomContext) {
      console.log(`🩺 SYMPTOM: Including symptom context for path "${symptomContext.symptomPath?.join(' → ')}"`);
    }

    try {
      const requestBody = { message };
      if (preloadedContext && preloadedContext.length > 0) {
        requestBody.preloaded_context = preloadedContext;
      }
      // ⭐ Add life theme context if provided
      if (lifeThemeContext) {
        requestBody.life_theme_context = {
          theme_id: lifeThemeContext.themeId,
          theme_name: lifeThemeContext.themeName,
          theme_description: lifeThemeContext.themeDescription,
          median_score: lifeThemeContext.medianScore,
          elevated_score: lifeThemeContext.elevatedScore,
          total_questions: lifeThemeContext.totalQuestions,
          elevation_level: lifeThemeContext.elevationLevel
        };
      }
      // ⭐ Add symptom context if provided
      if (symptomContext) {
        requestBody.symptom_path = symptomContext.symptomPath;
        requestBody.context_id = symptomContext.contextId || 'symptom';
        if (symptomContext.severityScore != null) {
          requestBody.severity_score = symptomContext.severityScore;
        }
      }
      // ⭐ Signal decision engine to reset session to OPENING state
      if (newConversation) {
        requestBody.new_conversation = true;
      }
      // ⭐ Include feeling score when user reports it via widget
      if (feelingScore !== null) {
        requestBody.feeling_score = feelingScore;
      }

      const response = await axios.post(
        `${API_BASE}${endpoint}`,
        requestBody,
        { headers: this.getHeaders(token) }
      );

      // Add security metadata to response
      response.data.security_level = SECURITY_LEVELS.ULTRA_SECURE;
      response.data.admin_access = false;
      
      return response.data;
    } catch (error) {
      console.error(`❌ Error sending ultra-secure message:`, error);
      throw error;
    }
  }

  // Get chat history with ultra-secure encryption only
  async getChatHistory(token, options = {}) {
    const { limit = 50, patientId, lifeThemeId } = options;

    // Always use ultra-secure endpoints for logged-in users
    const endpoint = '/api/ultra-secure-chat/ultra-secure-chat/history';
    let url = `${API_BASE}${endpoint}?limit=${limit}`;
    if (patientId) url += `&patient_id=${patientId}`;
    if (lifeThemeId) url += `&life_theme_id=${lifeThemeId}`;

    console.log(`🔐 Loading chat history via ultra-secure encryption from ${endpoint}`);
    if (lifeThemeId) {
      console.log(`🌱 Filtering for life theme ID: ${lifeThemeId}`);
    }

    try {
      const response = await axios.get(url, {
        headers: this.getHeaders(token)
      });

      // Return ultra-secure format with metadata
      return {
        conversations: response.data.conversations || [],
        security_level: SECURITY_LEVELS.ULTRA_SECURE,
        admin_access: false,
        encryption_type: 'user_specific'
      };
    } catch (error) {
      console.error(`❌ Error loading ultra-secure history:`, error);
      throw error;
    }
  }

  // Get important messages (crisis, breakthrough, reflection)
  async getImportantMessages(token, patientId = null) {
    try {
      let url = `${API_BASE}/api/ultra-secure-chat/ultra-secure-chat/history?limit=100`;
      if (patientId) url += `&patient_id=${patientId}`;

      console.log(`📋 Loading important messages from ${url}`);

      const response = await axios.get(url, {
        headers: this.getHeaders(token)
      });

      // Filter for important messages with special types or high importance
      const importantMessages = response.data.conversations
        .filter(conv => 
          conv.importance_score >= 4 || 
          ['crisis', 'breakthrough', 'reflection'].includes(conv.message_type)
        )
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); // Most recent first

      return {
        messages: importantMessages,
        security_level: SECURITY_LEVELS.ULTRA_SECURE,
        encryption_type: 'user_specific'
      };
    } catch (error) {
      console.error('❌ Error loading important messages:', error);
      return {
        messages: [],
        security_level: SECURITY_LEVELS.ULTRA_SECURE,
        message: 'Could not load important messages'
      };
    }
  }

  // Get chat stats 
  async getChatStats(token, patientId = null) {
    try {
      let url = `${API_BASE}/api/ultra-secure-chat/ultra-secure-chat/stats`;
      if (patientId) url += `?patient_id=${patientId}`;

      console.log(`📊 Loading chat stats from ${url}`);

      const response = await axios.get(url, {
        headers: this.getHeaders(token)
      });

      return response.data;
    } catch (error) {
      console.error('❌ Error loading stats:', error);
      return {
        total_messages: 0,
        messages_last_week: 0,
        security_level: SECURITY_LEVELS.ULTRA_SECURE,
        message: 'Could not load stats'
      };
    }
  }

  // AI Review methods - Same access pattern as ultra-secure chat
  async getPatientAIReviewSummary(token, patientId) {
    try {
      const url = `${API_BASE}/api/ai-review/patient/${patientId}/summary`;
      
      console.log(`🤖 Loading AI Review summary for patient ${patientId}`);
      
      const response = await axios.get(url, {
        headers: this.getHeaders(token)
      });

      return {
        ...response.data,
        security_level: SECURITY_LEVELS.ULTRA_SECURE,
        access_pattern: 'same_as_ultra_secure_chat'
      };
    } catch (error) {
      console.error('❌ Error loading AI Review summary:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied: You are not assigned to this patient');
      }
      throw error;
    }
  }

  async getPatientAIInsights(token, patientId, days = 7) {
    try {
      const url = `${API_BASE}/api/ai-review/patient/${patientId}/insights?days=${days}`;
      
      console.log(`🧠 Loading AI insights for patient ${patientId} (last ${days} days)`);
      
      const response = await axios.get(url, {
        headers: this.getHeaders(token)
      });

      return {
        ...response.data,
        security_level: SECURITY_LEVELS.ULTRA_SECURE,
        access_pattern: 'same_as_ultra_secure_chat'
      };
    } catch (error) {
      console.error('❌ Error loading AI insights:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied: You are not assigned to this patient');
      }
      throw error;
    }
  }

  async getPatientRecommendations(token, patientId) {
    try {
      const url = `${API_BASE}/api/ai-review/patient/${patientId}/recommendations`;
      
      console.log(`💡 Loading AI recommendations for patient ${patientId}`);
      
      const response = await axios.get(url, {
        headers: this.getHeaders(token)
      });

      return {
        ...response.data,
        security_level: SECURITY_LEVELS.ULTRA_SECURE,
        access_pattern: 'same_as_ultra_secure_chat'
      };
    } catch (error) {
      console.error('❌ Error loading AI recommendations:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied: You are not assigned to this patient');
      }
      throw error;
    }
  }

  async getPatientAnalyzedMessages(token, patientId, days = 30) {
    try {
      console.log(`💬 Loading chat messages for patient ${patientId} using ultra-secure API (working)`);
      
      // Step 1: Get decrypted messages using working ultra-secure-chat API
      const chatData = await this.getChatHistory(token, {
        limit: 100,
        patientId: patientId
      });

      if (!chatData.conversations || chatData.conversations.length === 0) {
        return {
          messages: [],
          security_level: SECURITY_LEVELS.ULTRA_SECURE,
          access_pattern: 'ultra_secure_chat_with_ai_review_scores'
        };
      }

      // Step 2: Get AI Review scores for these messages (if available)
      const messagesWithScores = await Promise.all(
        chatData.conversations.map(async (conv) => {
          try {
            // Try to get AI Review analysis for this message
            const aiAnalysis = await this.getMessageAIAnalysis(token, conv.id);
            
            return {
              message_id: conv.id,
              timestamp: conv.timestamp,
              user_message: conv.message,
              ai_response: conv.response,
              message_type: conv.message_type,
              importance_score: conv.importance_score,
              risk_level: conv.risk_level || 'low',
              detected_emotions: conv.detected_emotions || null,
              ai_confidence_score: conv.ai_confidence_score,
              
              // Add AI Review analysis if available
              ai_analysis: aiAnalysis || {
                improvement_score: 0,
                emotional_state: 'unknown',
                therapy_progress: 'stable',
                key_insights: [],
                risk_indicators: [],
                recommendation: '',
                analysis_timestamp: '',
                claude_confidence: 0
              }
            };
          } catch (aiError) {
            // If AI Review data not available, just include basic message data
            console.log(`No AI Review data for message ${conv.id}: ${aiError.message}`);
            
            return {
              message_id: conv.id,
              timestamp: conv.timestamp,
              user_message: conv.message,
              ai_response: conv.response,
              message_type: conv.message_type,
              importance_score: conv.importance_score,
              risk_level: conv.risk_level || 'low',
              detected_emotions: conv.detected_emotions || null,
              ai_confidence_score: conv.ai_confidence_score,
              
              // Basic AI analysis placeholder
              ai_analysis: {
                improvement_score: 0,
                emotional_state: 'unknown',
                therapy_progress: 'stable',
                key_insights: [],
                risk_indicators: [],
                recommendation: '',
                analysis_timestamp: '',
                claude_confidence: 0
              }
            };
          }
        })
      );

      // Filter messages from last X days and sort by importance/date
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      
      const recentMessages = messagesWithScores
        .filter(msg => new Date(msg.timestamp) >= cutoffDate)
        .sort((a, b) => {
          // Sort by importance first, then by date (newest first)
          if (a.importance_score !== b.importance_score) {
            return b.importance_score - a.importance_score;
          }
          return new Date(b.timestamp) - new Date(a.timestamp);
        });

      console.log(`✅ Loaded ${recentMessages.length} messages with AI Review scores using hybrid approach`);

      return {
        messages: recentMessages,
        total_messages: recentMessages.length,
        security_level: SECURITY_LEVELS.ULTRA_SECURE,
        access_pattern: 'ultra_secure_chat_with_ai_review_scores',
        source: 'hybrid_ultra_secure_chat_and_ai_review'
      };
    } catch (error) {
      console.error('❌ Error loading analyzed messages with hybrid approach:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied: You are not assigned to this patient');
      }
      throw error;
    }
  }

  // New helper function to get AI Review analysis for a specific message
  async getMessageAIAnalysis(token, messageId) {
    try {
      const url = `${API_BASE}/api/ai-review/message/${messageId}/analysis`;
      
      const response = await axios.get(url, {
        headers: this.getHeaders(token)
      });

      return response.data.analysis;
    } catch (error) {
      // AI Review analysis not available for this message - that's OK
      throw error;
    }
  }

  // Test ultra-secure access control
  async testUltraSecureSecurity(token) {
    try {
      const response = await axios.get(
        `${API_BASE}/ultra-secure/ultra-secure-chat/security-test`,
        { headers: this.getHeaders(token) }
      );

      return response.data;
    } catch (error) {
      console.error('❌ Error testing security:', error);
      throw error;
    }
  }

  // AI Review methods v2.0 for therapist interface (message-based)
  async getAIReview(token, messageId) {
    try {
      const url = `${API_BASE}/api/ai-review/${messageId}`;
      
      console.log(`🤖 Loading AI Review v2.0 for message ${messageId}`);
      
      const response = await axios.get(url, {
        headers: this.getHeaders(token)
      });

      return response.data;
    } catch (error) {
      console.error('❌ Error loading AI Review v2.0:', error);
      
      // Provide specific error messages for different scenarios
      if (error.response?.status === 404) {
        throw new Error('AI Review not found for this message');
      }
      if (error.response?.status === 403) {
        throw new Error('Access denied: Therapist access required');
      }
      if (error.response?.status === 500) {
        const serverError = error.response?.data?.detail || 'Internal server error';
        console.error('Server error details:', error.response?.data);
        throw new Error(`Server error: ${serverError}`);
      }
      if (error.response?.status === 401) {
        throw new Error('Authentication required');
      }
      if (error.code === 'NETWORK_ERROR' || !error.response) {
        throw new Error('Network error: Please check your connection');
      }
      
      // Generic error with as much detail as possible
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
  }

  async updateAIReview(token, messageId, updates) {
    try {
      const url = `${API_BASE}/api/ai-review/${messageId}`;
      
      console.log(`🤖 Updating AI Review v2.0 for message ${messageId}`, updates);
      
      const response = await axios.put(url, updates, {
        headers: this.getHeaders(token)
      });

      return response.data;
    } catch (error) {
      console.error('❌ Error updating AI Review v2.0:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied: Therapist access required');
      }
      throw error;
    }
  }

  async createAIReview(token, reviewData) {
    try {
      const url = `${API_BASE}/api/ai-review/`;
      
      console.log(`🤖 Creating AI Review v2.0 for message ${reviewData.message_id}`, reviewData);
      
      const response = await axios.post(url, reviewData, {
        headers: this.getHeaders(token)
      });

      return response.data;
    } catch (error) {
      console.error('❌ Error creating AI Review v2.0:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied: Therapist access required');
      }
      if (error.response?.status === 400) {
        throw new Error(error.response.data.detail || 'Invalid request');
      }
      throw error;
    }
  }

  async getPatientAIReviews(token, patientId) {
    try {
      const url = `${API_BASE}/api/ai-review/patient/${patientId}/ai-reviews`;
      
      console.log(`🤖 Loading AI Reviews for patient ${patientId}`);
      
      const response = await axios.get(url, {
        headers: this.getHeaders(token)
      });

      return response.data;
    } catch (error) {
      console.error('❌ Error loading patient AI Reviews:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied: You are not assigned to this patient');
      }
      throw error;
    }
  }

  async getFlaggedAIReviews(token) {
    try {
      const url = `${API_BASE}/api/ai-review/flagged`;
      
      console.log(`🚩 Loading flagged AI Reviews`);
      
      const response = await axios.get(url, {
        headers: this.getHeaders(token)
      });

      return response.data;
    } catch (error) {
      console.error('❌ Error loading flagged AI Reviews:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied: Therapist access required');
      }
      throw error;
    }
  }

  // Save a chat-generated insight to the user's anamnesis profile
  async saveChatInsight({ insight_text, category = 'pattern', life_theme_id = null }, token) {
    try {
      const response = await axios.post(
        `${API_BASE}/api/ultra-secure-chat/save-insight`,
        { insight_text, category, life_theme_id },
        { headers: this.getHeaders(token) }
      );
      return response.data;
    } catch (error) {
      console.error('❌ Error saving insight:', error);
      throw error;
    }
  }

  // Get security status (always ultra-secure)
  getSecurityStatus() {
    return {
      level: SECURITY_LEVELS.ULTRA_SECURE,
      is_ultra_secure: true,
      admin_can_decrypt: false,
      privacy_protection: 'Maximal',
      description: 'Bara du och din terapeut kan läsa dina meddelanden'
    };
  }
}

// Create singleton instance
const apiService = new APIService();

export default apiService;
